import { useCallback, useEffect, type KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowRightIcon, CopyIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { Loader } from '@rsos/base-components/Button/Loader';
import { ButtonText } from '@rsos/components/capstone/Location/styles/common.styles';
import { Button, Accordion } from '@rsos/components/capstone/base';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type {
  EtlTransportResponse,
  ComputedStatusItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { useTheme } from '@rsos/theme/styled';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import {
  trackStatusPageRender,
  trackOpenTransportLink,
  trackCopyTrackingLink,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { formatTimeWithUtcOffset } from '../../../utils/dateUtilities';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import { STATUS_PAGE_STATES } from '../AirAmbulanceForm/constants';
import {
  BannerWrapper,
  IconWrapper,
  BannerText,
} from '../Banner/StatusBanner.styles';
import styles from './AirAmbulanceEtlStatus.module.css';
import {
  FlexRow,
  FlexRowSpaceBetween,
  MarginWrapper,
  FullWidthDiv,
} from './AirAmbulanceEtlStatus.styles';
import AirAmbulanceEtlStatusControls from './AirAmbulanceEtlStatusControls';
import { useAirAmbulanceEtlStatus } from './hooks';

/**
 * Main status display component for ETL-based Air Ambulance requests.
 */
function AirAmbulanceEtlStatus({
  transport,
}: {
  transport: EtlTransportResponse;
}): JSX.Element {
  const { isLoading, statusPageState } = useAirAmbulanceEtlStatus();
  const {
    status,
    eta,
    aircraft,
    trackingUrl,
    pickup,
    dropoff,
    dispatchCenterName,
    dispatchCenterPhone,
    statuses,
  } = { ...transport.computed_fields };

  return (
    <div>
      <MarginWrapper>
        <EtlStatusBanner
          statusPageState={statusPageState}
          latestStatusDisplay={status}
          etaDisplay={eta}
        />
      </MarginWrapper>

      {trackingUrl && (
        <EtlTrackingInfo
          brandTail={aircraft}
          trackingUrl={trackingUrl}
          requestId={transport.gmr_transport_id}
        />
      )}

      <EtlTransportDetails
        pickupAddress={pickup}
        destination={dropoff}
        dispatchCenterName={dispatchCenterName}
        dispatchCenterPhone={dispatchCenterPhone}
      />

      {transport?.caller_id && (
        <EtlRelatedCall callerId={transport.caller_id} />
      )}

      <EtlStatusUpdates
        statuses={statuses}
        requestId={transport.gmr_transport_id}
        isLoading={isLoading}
      />

      {transport && <AirAmbulanceEtlStatusControls transport={transport} />}
    </div>
  );
}

/**
 * Renders a status timestamp in the timezone of the facility that reported it.
 *
 * GMR sends the status time in UTC alongside the facility's offset from UTC.
 * When there is no offset we have nothing better to go on than the timezone of
 * the browser.
 */
function formatTime(
  timestamp: string | undefined,
  offset?: string | null,
): string {
  if (!timestamp) return 'N/A';

  return formatTimeWithUtcOffset(timestamp, offset) ?? 'N/A';
}

interface EtlStatusBannerProps {
  statusPageState: (typeof STATUS_PAGE_STATES)[keyof typeof STATUS_PAGE_STATES];
  latestStatusDisplay: string | null;
  etaDisplay: string | null;
}

function EtlStatusBanner({
  statusPageState,
  latestStatusDisplay,
  etaDisplay,
}: EtlStatusBannerProps): JSX.Element {
  const IconComponent = statusPageState.icon;
  const displayText = latestStatusDisplay
    ? `${latestStatusDisplay}${etaDisplay ? ` - ${etaDisplay}` : ''}`
    : statusPageState.message;

  return (
    <BannerWrapper bgColor={statusPageState.bgColor} horizontal>
      <IconWrapper>
        <IconComponent color="background" height={16} width={16} />
      </IconWrapper>
      <BannerText>{displayText}</BannerText>
    </BannerWrapper>
  );
}

interface EtlTransportDetailsProps {
  pickupAddress: string | null;
  destination: string | null;
  dispatchCenterName: string | null | undefined;
  dispatchCenterPhone: string | null | undefined;
}

function EtlTransportDetails({
  pickupAddress,
  destination,
  dispatchCenterName,
  dispatchCenterPhone,
}: EtlTransportDetailsProps): JSX.Element {
  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent>
        <div className={styles.detailRows}>
          <div>
            <Text color="primaryText" fontWeight="bold" size="normal">
              Pickup
            </Text>
            <Text color="secondaryText" size="normal">
              {pickupAddress || 'TBD'}
            </Text>
          </div>
          <div>
            <Text color="primaryText" fontWeight="bold" size="normal">
              Destination
            </Text>
            <Text color="secondaryText" size="normal">
              {destination || 'TBD'}
            </Text>
          </div>
          {dispatchCenterName && (
            <div>
              <Text color="primaryText" fontWeight="bold" size="normal">
                Air Dispatch
              </Text>
              <Text color="secondaryText" size="normal">
                {dispatchCenterName}
              </Text>
            </div>
          )}
          {dispatchCenterPhone && (
            <div>
              <Text color="primaryText" fontWeight="bold" size="normal">
                Air Dispatch Phone
              </Text>
              <Text color="secondaryText" size="normal">
                {dispatchCenterPhone}
              </Text>
            </div>
          )}
        </div>
      </FullWidthContent>
    </MarginWrapper>
  );
}

interface EtlRelatedCallProps {
  callerId: string;
}

function EtlRelatedCall({ callerId }: EtlRelatedCallProps): JSX.Element {
  const dispatch = useDispatch();

  const handleViewRelatedCall = useCallback(() => {
    dispatch(setSelectedCallerID(callerId));
  }, [dispatch, callerId]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleViewRelatedCall();
      }
    },
    [handleViewRelatedCall],
  );

  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent
        className={styles.relatedCallCard}
        role="button"
        tabIndex={0}
        aria-label="View related call"
        onClick={handleViewRelatedCall}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.relatedCallHeader}>
          <Text color="primaryText" fontWeight="bold" size="normal">
            Related Call
          </Text>
          <ArrowRightIcon width={16} height={16} color="primaryText" />
        </div>
        <Text color="secondaryText" size="normal">
          {formatInternationalPhoneNumberHuman(callerId)}
        </Text>
      </FullWidthContent>
    </MarginWrapper>
  );
}

interface EtlTrackingInfoProps {
  /** @see AirAmbulanceEtlStatusResult.brandTail */
  brandTail: string | null;
  /** @see AirAmbulanceEtlStatusResult.trackingUrl */
  trackingUrl: string | null;
  requestId: string | null;
}

function EtlTrackingInfo({
  brandTail,
  trackingUrl,
  requestId,
}: EtlTrackingInfoProps): JSX.Element | null {
  const handleCopyLink = () => {
    if (trackingUrl) {
      copyToClipboard(trackingUrl);
    }
    trackCopyTrackingLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  const handleTrackAirAmbulance = () => {
    if (trackingUrl) {
      window.open(trackingUrl, '_blank', 'noopener,noreferrer');
    }
    trackOpenTransportLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  if (!brandTail && !trackingUrl) {
    return null;
  }

  return (
    <>
      {trackingUrl && (
        <div className={styles.trackingActions}>
          <Button
            type="button"
            grow
            noMargin
            btnType="tertiary"
            onClick={handleTrackAirAmbulance}
          >
            <ButtonText>Track Air Ambulance</ButtonText>
          </Button>
          <Button
            type="button"
            btnType="tertiary"
            noMargin
            title="Copy tracking link"
            onClick={handleCopyLink}
            style={{ aspectRatio: '1 / 1', padding: 0, minWidth: 'unset' }}
          >
            <CopyIcon />
          </Button>
        </div>
      )}
    </>
  );
}

interface StatusItemProps {
  status: ComputedStatusItem;
  index: number;
}

function StatusItem({ status, index }: StatusItemProps): JSX.Element {
  return (
    <FlexRow key={`${status.timestamp || index}-${index}`}>
      <Text color="primaryBase" size="normal">
        {formatTime(status.timestamp, status.offset)}
      </Text>
      <Text ml={2} color="secondaryText" size="normal">
        {status.status}
      </Text>
    </FlexRow>
  );
}

interface StatusUpdateHeaderProps {
  latestDisplayableStatus: ComputedStatusItem;
  isLoading: boolean;
}

function StatusUpdateHeader({
  latestDisplayableStatus,
  isLoading,
}: StatusUpdateHeaderProps): JSX.Element {
  const theme = useTheme();

  return (
    <div>
      <Text mb={2} color="primaryText" fontWeight="bold" size="normal">
        Status Updates
      </Text>
      <FlexRow>
        {latestDisplayableStatus?.status ? (
          <>
            <Text color="primaryBase" size="normal">
              {formatTime(
                latestDisplayableStatus.timestamp,
                latestDisplayableStatus.offset,
              )}
            </Text>
            <Text color="secondaryText" size="normal">
              {latestDisplayableStatus.status}
            </Text>
          </>
        ) : (
          <>
            <Text color="secondaryText" size="normal">
              Awaiting status updates...
            </Text>
            {isLoading && (
              <Loader size={16} color={theme.colors.secondaryText} />
            )}
          </>
        )}
      </FlexRow>
    </div>
  );
}

interface RemainingStatusesListProps {
  statuses: ComputedStatusItem[];
}

function RemainingStatusesList({
  statuses,
}: RemainingStatusesListProps): JSX.Element {
  return (
    <>
      {statuses
        .filter(status => status.status && status.timestamp)
        .map((status, index) => (
          <StatusItem
            key={`${status.timestamp || index}-${index}`}
            status={status}
            index={index}
          />
        ))}
    </>
  );
}

interface EtlStatusUpdatesProps {
  statuses: ComputedStatusItem[];
  requestId: string | null;
  isLoading: boolean;
}

function EtlStatusUpdates({
  statuses,
  requestId,
  isLoading,
}: EtlStatusUpdatesProps): JSX.Element {
  useEffect(() => {
    if (statuses.length <= 0) return;

    if (statuses[0].status && requestId) {
      trackStatusPageRender({
        requestId: requestId,
        status: statuses[0].status,
      });
    }
  }, [requestId, statuses]);

  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent>
        <div>
          <FlexRowSpaceBetween style={{ marginTop: 0, marginBottom: 0 }}>
            <FullWidthDiv>
              {statuses.length > 1 ? (
                <Accordion
                  header={
                    <StatusUpdateHeader
                      latestDisplayableStatus={statuses[0]}
                      isLoading={isLoading}
                    />
                  }
                  headerIcon="chevron"
                  interactionType="button"
                  noBorder
                  width="100%"
                  headerPadding="0"
                  padding="0"
                  content={
                    <RemainingStatusesList statuses={statuses.slice(1)} />
                  }
                />
              ) : (
                <StatusUpdateHeader
                  latestDisplayableStatus={statuses[0]}
                  isLoading={isLoading}
                />
              )}
            </FullWidthDiv>
          </FlexRowSpaceBetween>
        </div>
      </FullWidthContent>
    </MarginWrapper>
  );
}

export default AirAmbulanceEtlStatus;
